import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTable, usePagination, useFilters, useGlobalFilter, useSortBy } from 'react-table';

import InputItem from '../Forms/InputItem/InputItem';
import CustomSelect from '../Forms/CustomSelect/CustomSelect';

import { USER_ROLES } from '../../../util/constants/AppConstants';
import useOutsideClick from '../../../util/useOutsideClick';

import searchIcon from '../../../assets/images/icons/search.svg';
import filterIcon from '../../../assets/images/icons/filter.svg';
import activeFilterIcon from '../../../assets/images/icons/filter-active.svg';
import './Table.scss';

const Table = ({
  data,
  columns,
  clickHandler,
  clickHandlerTriggers = [],
  searchLabel = 'Search',
  filterByRole = false,
}) => {
  const [roleFilter, setRoleFilter] = useState([]);
  const [showRoleFilter, setShowRoleFilter] = useState(false);

  const filteredData = useMemo(() => {
    let filtered = data;

    if (filterByRole && roleFilter.length) {
      filtered = filtered.filter((row) => roleFilter.includes(row.role));
    }

    return filtered;
  }, [data, roleFilter, filterByRole]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    setGlobalFilter,
  } = useTable({ columns, data: filteredData }, useFilters, useGlobalFilter, useSortBy, usePagination);

  const { globalFilter, pageIndex, pageSize } = state;

  const filterRef = useRef(null);
  const filterIconRef = useRef(null);

  const handleRowClicked = (id) => {
    if (id) {
      clickHandler(id);
    }
  };

  const toggleRoleFilter = () => setShowRoleFilter((prev) => !prev);

  const handleFilterClick = (e) => {
    if ((filterRef.current && !filterRef.current.contains(e.target)) || !filterRef.current) {
      toggleRoleFilter();
    }
  };

  const handleRoleChange = (role) => {
    const newFilter = roleFilter.includes(role) ? roleFilter.filter((r) => r !== role) : [...roleFilter, role];
    setRoleFilter(newFilter);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pageIndex, pageSize]);

  useOutsideClick(filterRef, filterIconRef, showRoleFilter, toggleRoleFilter);

  return (
    <>
      <InputItem
        label={searchLabel}
        type="text"
        icon={searchIcon}
        onChange={(e) => setGlobalFilter(e.target.value || undefined)}
        value={globalFilter || ''}
      />
      <table {...getTableProps()} className="global-table-component">
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps()}>
                  {column.render('Header')}
                  {filterByRole && column.id === 'role' && (
                    <span className="filter" onClick={handleFilterClick} ref={filterIconRef}>
                      <img src={roleFilter.length > 0 ? activeFilterIcon : filterIcon} alt="Filter" />
                      {showRoleFilter && (
                        <div className="filter-dropdown" ref={filterRef}>
                          {USER_ROLES.map((role) => role.short).map((role) => (
                            <label key={role} className="filter-value">
                              <input
                                type="checkbox"
                                checked={roleFilter.includes(role)}
                                onChange={() => handleRoleChange(role)}
                              />
                              {role}
                            </label>
                          ))}
                        </div>
                      )}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td
                    {...cell.getCellProps()}
                    onClick={
                      clickHandlerTriggers.includes(cell.column.class)
                        ? () => handleRowClicked(row.original.id)
                        : undefined
                    }
                  >
                    {cell.render('Cell')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="pagination">
        <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
          {'<<'}
        </button>
        <button onClick={() => previousPage()} disabled={!canPreviousPage}>
          {'<'}
        </button>
        <button onClick={() => nextPage()} disabled={!canNextPage}>
          {'>'}
        </button>
        <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
          {'>>'}
        </button>
        <span>
          Page{' '}
          <strong>
            {pageOptions.length > 0 ? pageIndex + 1 : 0} of {pageOptions.length}
          </strong>
        </span>
        <CustomSelect
          options={[5, 10, 20].map((pageSize) => ({ label: `Show ${pageSize}`, value: pageSize }))}
          selectedOption={pageSize}
          setSelectedOption={(value) => setPageSize(Number(value))}
          defaultOption={`Show ${pageSize}`}
          isPagination={true}
          showLabel={false}
        />
      </div>
    </>
  );
};

export default Table;
